/**
 * Off-chain mirror of the pool's deposit Merkle tree.
 *
 * Was using `fixed-merkle-tree` (Tornado Cash's library) but its hash
 * function is sync and our Poseidon is async — it goes through a
 * wasm-based witness calculator so its output matches the circom
 * Poseidon byte-for-byte.
 * Re-implemented here as a minimal sparse Merkle tree with async hashing.
 *
 * Same public API the rest of the codebase already consumes:
 *   - buildMerkleTree({ leaves, height })  → tree
 *   - merkleProofForLeaf(tree, leaf)        → { pathElements, pathIndices, root }
 *   - merkleRoot(tree)                      → bigint
 *   - insertLeaf(tree, leaf)                → new root
 *   - bulkInsert(tree, leaves)              → new root
 */
import { MERKLE_TREE_HEIGHT, ZERO_VALUE } from "./constants";
import { buildPoseidon } from "./poseidon";
export class TonadoMerkleTree {
    height;
    /** Empty-subtree hash at each level. zeros[0] = ZERO_VALUE; zeros[height] = empty-tree root. */
    zeros;
    leaves;
    /** layers[level][i] = node at (level, i). layers[0] = leaves. */
    layers;
    constructor(height, zeros, leaves, layers) {
        this.height = height;
        this.zeros = zeros;
        this.leaves = leaves;
        this.layers = layers;
    }
    static async create(opts) {
        const cap = 2 ** opts.height;
        if (opts.leaves.length > cap) {
            throw new Error(`Cannot build tree: ${opts.leaves.length} leaves > capacity ${cap} at height ${opts.height}`);
        }
        const poseidon = await buildPoseidon();
        const zeros = [ZERO_VALUE];
        for (let i = 1; i <= opts.height; i++) {
            zeros.push(await poseidon([zeros[i - 1], zeros[i - 1]]));
        }
        const layers = [opts.leaves.slice()];
        for (let level = 0; level < opts.height; level++) {
            const prev = layers[level];
            const next = [];
            for (let i = 0; i < prev.length; i += 2) {
                const left = prev[i];
                const right = i + 1 < prev.length ? prev[i + 1] : zeros[level];
                next.push(await poseidon([left, right]));
            }
            layers.push(next);
        }
        return new TonadoMerkleTree(opts.height, zeros, opts.leaves.slice(), layers);
    }
    get root() {
        return this.layers[this.height].length === 0
            ? this.zeros[this.height]
            : this.layers[this.height][0];
    }
    /** Number of leaves currently inserted. */
    get leafCount() {
        return this.leaves.length;
    }
    /** Capacity = 2^height. Inserts beyond this throw. */
    get maxLeaves() {
        return 2 ** this.height;
    }
    indexOf(leaf) {
        return this.leaves.findIndex((x) => x === leaf);
    }
    path(idx) {
        if (idx < 0 || idx >= this.leaves.length) {
            throw new Error(`leaf index ${idx} out of range [0, ${this.leaves.length})`);
        }
        const pathElements = [];
        const pathIndices = [];
        let i = idx;
        for (let level = 0; level < this.height; level++) {
            const isRight = i % 2 === 1;
            const siblingIdx = isRight ? i - 1 : i + 1;
            const layer = this.layers[level];
            const sibling = siblingIdx < layer.length ? layer[siblingIdx] : this.zeros[level];
            pathElements.push(sibling);
            pathIndices.push(isRight ? 1 : 0);
            i = Math.floor(i / 2);
        }
        return { pathElements, pathIndices, root: this.root };
    }
    /**
     * Return the merkle path for the NEXT (not-yet-inserted) leaf position.
     * Used by the deposit flow to feed the merkle_update circuit: the same
     * path proves both `prevRoot = hash(ZERO_VALUE, path)` and
     * `newRoot = hash(commitment, path)`, because inserting a single leaf
     * doesn't change any sibling.
     */
    pathForNextInsert() {
        const idx = this.leaves.length;
        if (idx >= this.maxLeaves) {
            throw new Error(`Tree full: ${idx}/${this.maxLeaves} leaves at height ${this.height}`);
        }
        const pathElements = [];
        const pathIndices = [];
        let i = idx;
        for (let level = 0; level < this.height; level++) {
            const isRight = i % 2 === 1;
            const siblingIdx = isRight ? i - 1 : i + 1;
            const layer = this.layers[level];
            const sibling = siblingIdx < layer.length ? layer[siblingIdx] : this.zeros[level];
            pathElements.push(sibling);
            pathIndices.push(isRight ? 1 : 0);
            i = Math.floor(i / 2);
        }
        return { pathElements, pathIndices, root: this.root };
    }
    /** Append a leaf and rebuild from scratch. O(N) but our N is bounded. */
    async insertLeaf(leaf) {
        if (this.leaves.length >= this.maxLeaves) {
            throw new Error(`Tree full: ${this.leaves.length}/${this.maxLeaves} leaves at height ${this.height}`);
        }
        this.leaves.push(leaf);
        const poseidon = await buildPoseidon();
        this.layers = [this.leaves.slice()];
        for (let level = 0; level < this.height; level++) {
            const prev = this.layers[level];
            const next = [];
            for (let i = 0; i < prev.length; i += 2) {
                const left = prev[i];
                const right = i + 1 < prev.length ? prev[i + 1] : this.zeros[level];
                next.push(await poseidon([left, right]));
            }
            this.layers.push(next);
        }
        return this.root;
    }
    async bulkInsert(more) {
        if (this.leaves.length + more.length > this.maxLeaves) {
            throw new Error(`bulkInsert overflows tree: ${this.leaves.length} + ${more.length} > ${this.maxLeaves}`);
        }
        for (const leaf of more) {
            this.leaves.push(leaf);
        }
        const rebuilt = await TonadoMerkleTree.create({
            leaves: this.leaves,
            height: this.height,
        });
        this.layers = rebuilt.layers;
        return this.root;
    }
}
export async function buildMerkleTree(opts = {}) {
    return TonadoMerkleTree.create({
        leaves: opts.leaves ?? [],
        height: opts.height ?? MERKLE_TREE_HEIGHT,
    });
}
export async function merkleProofForLeaf(tree, leaf) {
    const idx = tree.indexOf(leaf);
    if (idx < 0) {
        throw new Error(`Leaf not present in tree: ${leaf.toString().slice(0, 16)}…`);
    }
    return tree.path(idx);
}
export function merkleRoot(tree) {
    return tree.root;
}
export async function insertLeaf(tree, leaf) {
    return tree.insertLeaf(leaf);
}
export async function bulkInsert(tree, leaves) {
    return tree.bulkInsert(leaves);
}
//# sourceMappingURL=merkleTree.js.map